import React, { useState, useRef } from 'react'
import { connect } from 'react-redux'
import {
  IconButton,
  List,
  ListItem,
  ListItemText,
  TableRow,
  TableCell,
  Collapse,
  makeStyles,
  Divider,
  ClickAwayListener,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core'
import ExpandMore from '@material-ui/icons/ExpandMore'
import ExpandLess from '@material-ui/icons/ExpandLess'
import SettingsIcon from '@material-ui/icons/Settings'

import SettingsPopper from '../../../lib/components/SettingsPopper'
import { hasPermission } from '../../../lib/components/form/RestrictedComponent'
import queryString from '../../../utils/QueryString'
import AddUsersDialog from './AddUsersDialog'
import { ADD_CONTENT_GROUP_USER, DELETE_CONTENT_GROUP, REMOVE_CONTENT_GROUP_USER } from '../../../redux/data/types'
import actions from '../../../redux/actions'
import config from '../../../config'

const useStyles = makeStyles(theme => ({
  tableCell: {
    verticalAlign: 'baseline',
    minWidth: '25%',
    borderRight: '1px solid rgba(0,0,0,0.1)'
  },
  listItem: {
    paddingLeft: '1rem'
  },
  dataList: {
    maxHeight: '10rem',
    overflow: 'auto',
    minHeight: '1rem',
    backgroundColor: 'rgb(248,248,248)',
    borderRadius: '5px'
  },
  expandButton: {
    borderRadius: '7px',
    padding: 0,
  },
  categoryButtonLink: {
    textTransform: 'none'
  },
  assignedList: {
    padding: 0
  },
  warning: {
    color: '#721c24',
    '&:hover': {
      backgroundColor: '#f8d7da'
    },
    '&.disabled:hover': {
      backgroundColor: 'lightgray'
    },
  },
  innerListHeader: {
    textAlign: 'right',
    marginRight: '1rem'
  }
}))

const DeleteConfirmation = props => {
  const { open, onClose, onDelete, name } = props
  return <Dialog open={open} onClose={onClose}>
    <DialogTitle>
      Permanently Delete Content Group?
    </DialogTitle>
    <DialogContent>
      Are you sure you want to permanently delete the content group <b>{name}</b>?
    </DialogContent>
    <DialogActions>
      <Button
        onClick={onClose}
        variant="contained"
        color="primary"
      >
        Cancel
      </Button>
      <Button
        onClick={onDelete}
        variant="contained"
        color="secondary"
      >
        Delete Forever
      </Button>
    </DialogActions>
  </Dialog>
}

const GroupTableRow = props => {
  const { contentGroup, selectGroup, assignUsersToGroup, users, deleteContentGroup, removeUserFromGroup } = props
  const classes = useStyles()
  const [categoriesExpanded, setCategoriesExpanded] = useState(false)
  const [usersExpanded, setUsersExpanded] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const toggleCategoriesExpanded = () => setCategoriesExpanded(!categoriesExpanded)
  const toggleUsersExpanded = () => setUsersExpanded(!usersExpanded)
  const settingsRef = useRef(null)
  const [showAddUsersDialog, setShowAddUsersDialog] = useState(false)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)

  const numCategories = contentGroup.GroupCategories.length
  const numUsers = contentGroup.GroupUsers.length
  const groupId = contentGroup.Group.groupID

  const openDetails = () => {
    const qs = queryString.parse(location.search)
    queryString.set({ ...qs, contentGroup: groupId })
    selectGroup(groupId)
  }

  const settingActions = [
    {
      text: 'View Details',
      disabled: !hasPermission('account_contentGroups.read'),
      onClick: () => openDetails()
    },
    {
      text: 'Assign Users',
      disabled: !hasPermission('account_contentGroups.update'),
      onClick: () => setShowAddUsersDialog(true)
    },
    {
      text: 'Delete Group',
      disabled: !hasPermission('account_contentGroups.delete'),
      onClick: () => setShowDeleteConfirmation(true),
      className: classes.warning
    }
  ]

  const handleDeleteGroup = () => {
    deleteContentGroup(groupId)
    setShowDeleteConfirmation(false)
  }

  return <TableRow>
    <TableCell size="small" className={classes.tableCell}>
      <Button color="primary" onClick={openDetails} className={classes.categoryButtonLink}>
        {contentGroup.Group.groupName}
      </Button>
    </TableCell>
    <TableCell size="small" className={classes.tableCell}>
      {contentGroup.Group.groupDescription}
    </TableCell>
    <TableCell size="small" className={classes.tableCell}>
      <List component="nav" className={classes.assignedList}>
        <ListItem className={classes.expandButton} button onClick={toggleCategoriesExpanded}>
          <ListItemText className={classes.innerListHeader} primary={`Assigned (${numCategories})`} />
          {categoriesExpanded ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        <Collapse in={categoriesExpanded} timeout="auto" unmountOnExit>
          <List className={classes.dataList} component="div" dense={true} disablePadding={true}>
            {contentGroup.GroupCategories.map((category, i) => {
              return <React.Fragment key={'category item:' + i}>
                <Divider />
                <ListItem className={classes.listItem}>
                  <span>{category.displayName}</span>
                </ListItem>
              </React.Fragment>
            })}
          </List>
        </Collapse>
      </List>
    </TableCell>
    <TableCell size="small" className={classes.tableCell}>
      <List component="nav" className={classes.assignedList}>
        <ListItem className={classes.expandButton} button onClick={toggleUsersExpanded}>
          <ListItemText className={classes.innerListHeader} primary={`Assigned (${numUsers})`} />
          {usersExpanded ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        <Collapse in={usersExpanded} timeout="auto" unmountOnExit>
          <List className={classes.dataList} component="div" dense={true} disablePadding={true}>
            {contentGroup.GroupUsers.map((user, i) => {
              return <React.Fragment key={'category item:' + i}>
                <Divider />
                <ListItem className={classes.listItem}>
                  <span>{user.firstName + ' ' + user.lastName}</span>
                </ListItem>
              </React.Fragment>
            })}
          </List>
        </Collapse>
      </List>
    </TableCell>
    <TableCell padding="checkbox" className={classes.tableCell}>
      <ClickAwayListener onClickAway={() => setSettingsOpen(false)}>
        <IconButton onClick={() => setSettingsOpen(true)}>
          <SettingsIcon ref={settingsRef} />
        </IconButton>
      </ClickAwayListener>
    </TableCell>
    <SettingsPopper
      onClose={() => setSettingsOpen(false)}
      parentRef={settingsRef}
      open={settingsOpen}
      actions={settingActions}
    />
    <AddUsersDialog
      isOpen={showAddUsersDialog}
      users={users}
      onAdd={users => assignUsersToGroup(users, groupId)}
      onRemove={user => removeUserFromGroup(user, groupId)}
      onClose={() => setShowAddUsersDialog(false)}
      groupUsers={contentGroup.GroupUsers}
      groupID={contentGroup.Group.groupID}
    />
    <DeleteConfirmation
      open={showDeleteConfirmation}
      onClose={() => setShowDeleteConfirmation(false)}
      onDelete={handleDeleteGroup}
      name={contentGroup.Group.groupName}
    />
  </TableRow>
}

const mapStateToProps = state => {
  const clientID = state.data.user.clientId
  const users = state.data.users
  return { clientID, users }
}

const mapDispatchToProps = dispatch => ({
  callDispatch: func => dispatch(func),
})

const mergeProps = (stateProps, _dispatchProps, ownProps) => {
  const dispatch = func => _dispatchProps.callDispatch(func)
  const assignUsersToGroup = (users, groupId) => {
    // users requests must be individual
    let promises = []
    for (let i = 0; i < users.length; i++) {
      const user = users[i]
      const postData = {
        clientID: stateProps.clientID,
        groupID: groupId,
        userID: user.userId
      }
      // console.log('request ' + i + ' ' + JSON.stringify(postData))
      promises.push(dispatch(actions.request.sendRequest({
        type: ADD_CONTENT_GROUP_USER,
        url: `${config.uris.crud}Clientcontentgroups/addUser`,
        postData
      })))
    }
    Promise.all(promises)
  }

  const deleteContentGroup = groupID => {
    const postData = {
      clientID: stateProps.clientID,
      groupID: groupID
    }
    dispatch(actions.request.sendRequest({
      type: DELETE_CONTENT_GROUP,
      url: `${config.uris.crud}Clientcontentgroups/deleteGroup`,
      postData
    }))
  }

  const removeUserFromGroup = (user, groupId) => {
    // users requests must be individual
    const postData = {
      clientID: stateProps.clientID,
      groupID: groupId,
      userID: user.userId
    }
    dispatch(actions.request.sendRequest({
      type: REMOVE_CONTENT_GROUP_USER,
      url: `${config.uris.crud}Clientcontentgroups/removeUser`,
      postData
    }))
  }

  return { ...stateProps, assignUsersToGroup, deleteContentGroup, removeUserFromGroup, ...ownProps }
}

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(GroupTableRow)
